<script>
    import dressing from "@intellectability/platform-common";
    import {Button, Chart, Date, Icon, LazyLoader, MessageOfTheDayModal} from "@intellectability/platform-common/sv";

    import {Card, CardBody, CardFooter} from "@sveltestrap/sveltestrap";
    import {fade, fly, scale} from "svelte/transition";

    import {
        bounceOut,
        quintOut,
        backOut,
        expoOut
    } from "svelte/easing";
    // import Chart from "../../../Dashboard/src/lib/Chart.svelte";
    import DataTableCard from "./DataTableCard.svelte";
    import RunReportModal from "./RunReportModal.svelte";

    /*
        Card types are:
            Summary, DataTable

        Card subtypes are:
            Chart, Informational, List, Popup, Report

         Keyed Card Names:
            Client Center, Message Center, Message of the Day
     */

    /** @typedef CardData
     *  @property {string} card_description
     *  @property {string} card_entity_status
     *  @property {string} card_entity_type
     *  @property {boolean} card_mandatory
     *  @property {string} card_name
     *  @property {number} card_report_id
     *  @property {string} card_size
     *  @property {string} card_subtype
     *  @property {string} card_type
     *  @property {string} card_url
     *
     *  @property {boolean} enabled
     *  @property {number} id
     *  @property {number} order
     *  @property {string} report_id
     *  @property {number} fixedHeight
     *
     */

    /**
     * @typedef DashboardProps
     * @property {CardData} cardData
     * @property {?function} [onloaded]
     * @property {?number} [delayAnimationMS=0]
     * @property {?number} [fixedHeight]
     * @property {?number} [highlight_rank] - a number between 1 and 6. Modifies the color of the card background. Purely visual.
     */

    /** @type DashboardProps */
    let {
        cardData,
        onloaded,
        delayAnimationMS = 0,
        fixedHeight = null,
        highlight_rank: rank = null
    } = $props();

    let id = $derived(cardData.id);
    let cardName = $derived(cardData.card_name);
    let cardType = $derived(cardData.card_type);
    let cardSubType = $derived(cardData.card_subtype);
    let cardURL = $derived(cardData.card_url ?? "");
    let cardReportId = $derived(cardData.card_report_id ?? 0);

    let hasReport = $derived(cardReportId > 0);
    let hasLink = typeof cardData.card_url === "string" && cardData.card_url.length > 0;

    let loading = $state(true);

    /** @type * */
    let fetchedData = $state.raw(null);

    let delay = $derived.by(function () {
        if (Number.isInteger(delayAnimationMS)) {
            return delayAnimationMS;
        }
        return 0;
    });

    let easing = getRandomEasing();

    function loadCardData() {

        const body = {
            card_id: id,
            context: "dashboard_loading"
        };

        dressing.post(`/dashboard/cards/data`, body)
            .then(({data: res}) => {

                const {success, data} = res;
                if (success === true) {
                    fetchedData = data;
                    onloaded?.(data);
                }
                loading = false;
            })
            .catch(error => {
                    console.log(error);
                    loading = false;
                }
            );
    }

    function getRandomEasing() {
        const easings = [
            bounceOut,
            // elasticOut,
            // quadOut,
            quintOut,
            backOut,
            // cubicOut,
            // sineOut,
            expoOut
            // quartOut,
            // circOut
        ];

        return easings[Math.round(Math.random() * (easings.length - 1))];

    }

    function colorByRank(rank) {
        switch (rank) {
            case 1:
                return "var(--hrst-blue-50)";
            case 2:
                return "var(--hrst-red-50)";
            case 3:
                return "var(--hrst-error-50)";
            case 4:
                return "var(--hrst-green-50)";
            case 5:
                return "var(--hrst-red-50)";
            case 6:
                return "var(--hrst-error-50)";
            default:
                return "";
        }
    }

</script>

{#if cardType === "DataTable"}
    <div class="rounded">
        <!--  Account for tab panel header when setting height -->
        <div style="height: {fixedHeight - 65}px">
            <LazyLoader onvisible={loadCardData}>
                <DataTableCard {fixedHeight} {cardData} {loading}
                               tableData={fetchedData?.value}></DataTableCard>
            </LazyLoader>
        </div>
    </div>
{:else}
    <div class="rounded">
        <div in:fade|global={{duration: 500, delay}} style="">
            <div in:scale|global={{duration: 650, start: .9, easing, delay }}>
                {#if cardSubType === "Popup" && cardName === "Message of the Day"}
                    {@render messageOfTheDayCard(loading, cardData)}
                {:else if cardSubType === "Chart"}
                    {@render chartCard(loading, cardData)}
                {:else if cardType === "Summary" && cardSubType === "Report"}
                    {@render reportCard(loading, cardData)}
                {:else if cardSubType === "Informational"}
                    {@render informationalCard(loading, cardData)}
                {/if}
            </div>
            <!-- render nothing if not found -->
        </div>
    </div>
{/if}


{#snippet messageOfTheDayCard(loading, cardData = {})}
    {@const countUnread = fetchedData?.value?.unread_messages ?? "0?"}
    {@const canMarkRead = fetchedData?.value?.can_mark_read ?? false}
    <!-- Show a red border highlight if messages are awaiting -->
    {@const borderHighlight = 2 > 0}
    <Card style={`min-height: ${fixedHeight}px; `
                    + (borderHighlight ? "border: 1px solid var(--hrst-red-300)" : "")}>
        {@render idElement(id)}
        <CardBody>
            <div class="text-center">
                <h3 class="motd-title m-0">Message</h3>
                <h3 class="motd-title">of the Day</h3>

                {#if canMarkRead}
                    <LazyLoader onvisible={loadCardData}>
                        <div class="d-flex flex-column align-items-center">
                            Unread messages
                            <div>
                                {#if loading}
                                    <wa-badge variant="neutral" appearance="filled outlined" pill>
                                        <!-- Leave the zero-width non-joiner please -->
                                        &zwnj;
                                        <!-- Leave the zero-width non-joiner please-->
                                        <wa-spinner style=" --indicator-color: var(--hrst-blue-200);"></wa-spinner>
                                    </wa-badge>
                                {:else if countUnread > 0}
                                    <wa-badge variant="danger" pill pulse>
                                        {countUnread}
                                    </wa-badge>
                                {:else}
                                    <wa-badge variant="success" pill appearance="filled outlined">0</wa-badge>
                                {/if}
                            </div>
                        </div>
                    </LazyLoader>
                {/if}
            </div>
        </CardBody>
        <CardFooter class="p-0">
            <MessageOfTheDayModal modalOpenButton={detailsButton}/>
        </CardFooter>
    </Card>
{/snippet}

{#snippet reportCard(loading, cardData = {})}
    {@const crossfade_distance = 10}
    {@const value = fetchedData?.value ?? "0?"}
    <LazyLoader onvisible={loadCardData}>
        <Card style={("background-color:" + colorByRank(rank) ?? "") + `; min-height: ${fixedHeight}px;`}>
            {@render idElement(id)}
            <CardBody>
                <div class="crossfade">
                    {#if loading}
                        <div class="crossfade-item"
                             in:fly={{ x: -crossfade_distance, duration: 400, delay: 150 }}
                             out:fly={{ x: -crossfade_distance, duration: 400 }}>
                            <div class="dashboard-card-value-loading">
                                <div class="placeholder-wave" style="height: 35px;">
                                    <span class="placeholder rounded h-100" style="width: 80px;"></span>
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="crossfade-item"
                             in:fly={{ x: crossfade_distance, duration: 400, delay: 150 }}
                             out:fly={{ x: crossfade_distance, duration: 400 }}>
                            <div class="dashboard-card-value">
                                {value}
                            </div>
                        </div>
                    {/if}
                    <div>
                        <div class="text-center">
                            {cardData.card_name}
                        </div>
                        {@render whenRun()}
                    </div>
                </div>
            </CardBody>
            {#if hasLink && cardURL.length > 0}
                {@render detailsButton(() => window.location.href = cardURL)}
            {:else if hasReport}
                <RunReportModal
                        cardID={cardData.id}
                        disabled={value ===0}
                        modalOpenButton={detailsButton}
                        reportTitle={cardName}
                />
            {/if}
        </Card>
    </LazyLoader>
{/snippet}

{#snippet informationalCard(loading, cardData = {})}
    {@const crossfade_distance = 10}
    {@const value = fetchedData?.value ?? "0?"}
    <LazyLoader onvisible={loadCardData}>
        <Card style={("background-color:" + colorByRank(rank) ?? "") + `; min-height: ${fixedHeight}px;`}>
            {@render idElement(id)}
            <CardBody>
                <div class="crossfade">
                    {#if loading}
                        <div class="crossfade-item"
                             in:fly={{ x: -crossfade_distance, duration: 400, delay: 150 }}
                             out:fly={{ x: -crossfade_distance, duration: 400 }}>
                            <div class="dashboard-card-value-loading">
                                <div class="placeholder-wave" style="height: 35px;">
                                    <span class="placeholder rounded h-100" style="width: 80px;"></span>
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="crossfade-item"
                             in:fly={{ x: crossfade_distance, duration: 400, delay: 150 }}
                             out:fly={{ x: crossfade_distance, duration: 400 }}>
                            <div class="dashboard-card-value">
                                {value}
                            </div>
                        </div>
                    {/if}
                    <div>
                        <div class="text-center">
                            {cardData.card_name}
                        </div>
                        {@render whenRun()}
                    </div>
                </div>
            </CardBody>
            {#if hasLink && cardURL.length > 0}
                {@render detailsButton(() => window.location.href = cardURL)}
            {:else if hasReport}
                {@render detailsButton(
                    () => window.location.href = "index.php?action=40010&uid=14&tab=reports&report_id=" + cardData.card_report_id)}
            {/if}
        </Card>
    </LazyLoader>
{/snippet}

{#snippet chartCard(loading, cardData = {})}
    {@const crossfade_distance = 10}
    <LazyLoader onvisible={loadCardData}>
        <Card style={("background-color:" + colorByRank(rank) ?? "") + `; min-height: ${fixedHeight}px;`}>
            {@render idElement(id)}
            <CardBody>
                <div class="crossfade">
                    {#if loading}
                        <div class="crossfade-item"
                             in:fly={{ x: -crossfade_distance, duration: 400, delay: 150 }}
                             out:fly={{ x: -crossfade_distance, duration: 400 }}>
                            <div class="dashboard-card-value-loading">
                                <div class="placeholder-wave" style="height: 35px;">
                                    <span class="placeholder rounded h-100" style="width: 80px;"></span>
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="crossfade-item"
                             in:fly={{ x: crossfade_distance, duration: 400, delay: 150 }}
                             out:fly={{ x: crossfade_distance, duration: 400 }}>
                            <div class="dashboard-card-value">
                                <Chart
                                        data={fetchedData?.value?.chart_data}
                                        height="80"
                                        showLegend={false}
                                ></Chart>
                            </div>
                        </div>
                    {/if}
                    <div class="text-center">
                        {cardData.card_name}
                    </div>
                    {@render whenRun()}
                </div>
            </CardBody>
            {#if hasLink && cardURL.length > 0}
                {@render detailsButton(() => window.location.href = cardURL)}
            {:else if hasReport}
                {@render detailsButton(
                    () => window.location.href = "index.php?action=40010&uid=14&tab=reports&report_id=" + cardData.card_report_id)}
            {/if}
        </Card>
    </LazyLoader>
{/snippet}

{#snippet detailsButton(handler, text = "View Details", disabled = false)}
    <CardFooter class="p-0">
        <Button appearance="plain"
                variant="neutral"
                class="w-100 rounded-top-0"
                {disabled}
                onclick={() => handler?.()}>
            <Icon icon="table"/>
            {text}
        </Button>
    </CardFooter>
{/snippet}

{#snippet idElement(id)}
    <span class="position-absolute text-secondary-500" style="top: 5px; left: 7px; font-size: 11px;">{id}</span>
{/snippet}

{#snippet whenRun()}
    <!-- Do we need this?   -->
    <!--    <div class="d-inline-block justify-content-center last-updated-text pt-2">-->
    <!--        <span>Since<Date mysqlDateString={"1999-02-01"} showTimezone={true} class="mx-1"/></span>-->
    <!--        <button class="btn btn-sm shadow-none px-2 py-0">-->
    <!--            <Icon icon="arrows-rotate"/>-->
    <!--        </button>-->
    <!--    </div>-->
{/snippet}

<style>

    /* create a crossfade transition between the value placeholder */
    .crossfade {
        display: grid;
    }

    .crossfade-item {
        /*height: 2em;*/
        grid-column: 1/2;
        grid-row: 1/2;
        margin: .5em 0;
    }

    .dashboard-card-value-loading {
        /*height: 2em;*/
        text-align: center;
        color: rgba(var(--bs-primary-rgb), 35%);
    }

    .dashboard-card-value {
        line-height: 1;
        font-size: 2rem;
        color: var(--bs-primary);
        text-align: center;
    }

    :global(.last-updated-text) {
        font-size: 0.7rem;
        color: var(--bs-gray-600);
    }
</style>
